const BASE_URL = "https://tweets-ai.onrender.com"; //"https://tweets-ai.onrender.com"; //"http://192.168.1.120:3001"; // Update if needed

// Helper function to get admin key from URL
function getAdminKey() {
  // First try to get from hash parameters
  const hashParams = new URLSearchParams(
    window.location.hash.split("?")[1] || ""
  );
  const adminKeyFromHash = hashParams.get("adminKey");

  // If not found in hash, try regular query parameters
  const params = new URLSearchParams(window.location.search);
  const adminKeyFromQuery = params.get("adminKey");

  // If found in either URL parameter, store it in localStorage
  const adminKey = adminKeyFromHash || adminKeyFromQuery;
  if (adminKey) {
    localStorage.setItem("adminKey", adminKey);
    return adminKey;
  }

  // If not in URL, try to get from localStorage
  return localStorage.getItem("adminKey");
}

export async function fetchPosts() {
  const adminKey = getAdminKey();
  const response = await fetch(
    `${BASE_URL}/api/posts${adminKey ? `?adminKey=${adminKey}` : ""}`,
    {
      credentials: "include", // ★ send uid cookie
    }
  );
  return response.json();
}

export async function verifyAdminKey(adminKey) {
  const response = await fetch(`${BASE_URL}/api/verify-admin`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ adminKey }),
  });

  if (!response.ok) {
    throw new Error("Failed to verify admin key");
  }

  const { isValid } = await response.json();
  return isValid;
}

export async function createPost(
  text,
  imageURL,
  AIProfileId,
  componentId = null,
  imagePrompt = null
) {
  const adminKey = getAdminKey();
  console.log("imageURL: ", imageURL);
  const response = await fetch(`${BASE_URL}/api/posts/create`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      text,
      imageURL,
      AIProfileId,
      componentId,
      imagePrompt,
      adminKey, // Include adminKey in the request body
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to create post");
  }

  return response.json();
}

export async function generateAIContent(existingText, imagePrompt) {
  const adminKey = getAdminKey(); // ① grab it once
  const response = await fetch(
    `${BASE_URL}/api/posts/generate${adminKey ? `?adminKey=${adminKey}` : ""}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ existingText, imagePrompt, adminKey }), // ②
    }
  );
  if (!response.ok) {
    throw new Error("Failed to generate AI content.");
  }
  return response.json(); // { generatedText, imageUrl, post } (depending on your server return)
}

// Add this function
export async function addComment(postId, comment, author, userInfo) {
  const response = await fetch(`${BASE_URL}/api/posts/${postId}/comment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      text: comment, // Changed from 'comment' to 'text' to match server expectation
      author,
      userInfo,
    }),
  });
  if (!response.ok) {
    throw new Error("Failed to add comment");
  }
  return response.json(); // Return the updated post
}

// Fetch a single post by ID (optional for expanded feature)
export async function fetchPostById(postId) {
  const uid = getOrCreateUserId();
  const response = await fetch(
    `${BASE_URL}/api/posts/${postId}?userId=${encodeURIComponent(uid)}`
  );
  const post = await response.json();

  // The server now provides likeCount and userHasLiked directly
  return post;
}

// 1) Fetch all AI profiles
export async function fetchAIProfiles() {
  const adminKey = getAdminKey();
  const response = await fetch(
    `${BASE_URL}/api/ai-profiles${adminKey ? `?adminKey=${adminKey}` : ""}`,
    {
      credentials: "include", // ★ send uid cookie
    }
  );
  if (!response.ok) {
    throw new Error("Failed to fetch AI profiles");
  }
  return response.json();
}

// 2) Fetch one AI profile by ID
export async function fetchAIProfileById(id) {
  const adminKey = getAdminKey();
  const response = await fetch(
    `${BASE_URL}/api/ai-profiles/${id}${
      adminKey ? `?adminKey=${adminKey}` : ""
    }`,
    {
      credentials: "include", // ★ send uid cookie
    }
  );
  if (!response.ok) {
    throw new Error("Failed to fetch AI profile");
  }
  return response.json();
}

export async function generateAIProfileContent(id, userInput, imagePrompt) {
  const response = await fetch(`${BASE_URL}/api/ai-profiles/${id}/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userInput, imagePrompt }),
  });
  if (!response.ok) {
    throw new Error("Failed to generate AI content");
  }
  return response.json();
}

/**
 * Fetch the dynamic component code from the server
 * by its 'componentId', which corresponds to ComponentCode.id in the backend.
 */
export async function fetchComponentCode(componentId) {
  const response = await fetch(`${BASE_URL}/api/components/${componentId}`);
  if (!response.ok) {
    // e.g. 404 if component not found
    const errorText = await response.text();
    console.error("Failed to fetch component code, server said:", errorText);
    throw new Error("Failed to fetch component code");
  }
  // Return the { id, source, description }
  const data = await response.json();
  return data;
}

export async function storeManualComponent(id, source, description) {
  const adminKey = getAdminKey();
  const response = await fetch(`${BASE_URL}/api/components/manual`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      id,
      source,
      description,
      adminKey,
    }),
  });
  if (!response.ok) throw new Error("Failed to store manual component");
  return response.json();
}

export function getOrCreateUserId() {
  let userId = localStorage.getItem("userId");
  if (!userId) {
    userId = "user-" + Date.now() + "-" + Math.floor(Math.random() * 10000);
    localStorage.setItem("userId", userId);
  }
  return userId;
}

export async function toggleLikePost(postId) {
  const response = await fetch(`${BASE_URL}/api/posts/${postId}/toggle-like`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId: getOrCreateUserId() }),
  });
  if (!response.ok) {
    throw new Error("Failed to toggle like");
  }
  // Returns { post, likesCount }
  return response.json();
}

export async function togglePostVisibility(postId) {
  const adminKey = getAdminKey();
  const response = await fetch(
    `${BASE_URL}/api/posts/${postId}/toggle-visibility`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ adminKey }),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to toggle post visibility");
  }

  return response.json();
}

export async function toggleAIProfileVisibility(profileId) {
  const adminKey = getAdminKey();
  if (!adminKey) {
    throw new Error("Admin key is required to toggle visibility");
  }

  const response = await fetch(
    `${BASE_URL}/api/ai-profiles/${profileId}/toggle-visibility`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ adminKey }),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to toggle AI profile visibility");
  }

  return response.json();
}

export async function createAIProfile(name, prompt, description) {
  const adminKey = getAdminKey();
  const response = await fetch(`${BASE_URL}/api/ai-profiles/create`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name,
      prompt,
      description,
      adminKey,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to create AI profile");
  }

  return response.json();
}

export async function generateAIImage(imagePrompt) {
  const adminKey = getAdminKey();
  const response = await fetch(
    `${BASE_URL}/api/posts/generate${adminKey ? `?adminKey=${adminKey}` : ""}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ imagePrompt, adminKey }),
    }
  );
  if (!response.ok) {
    throw new Error("Failed to generate image");
  }
  const data = await response.json();
  return data.imageUrl;
}

export async function getAIResponse(prompt) {
  const adminKey = getAdminKey();
  console.log("getAIResponse called with:", {
    promptLength: prompt?.length,
    hasAdminKey: !!adminKey,
    adminKeyPreview: adminKey ? adminKey.substring(0, 10) + "..." : "none",
  });

  const payload = { prompt, adminKey };
  console.log("Sending payload:", {
    ...payload,
    adminKey: adminKey ? "[REDACTED]" : "none",
  });

  const response = await fetch(`${BASE_URL}/api/ai-profiles/generate-raw`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  console.log("Response status:", response.status);

  if (!response.ok) {
    const errorText = await response.text();
    console.error("getAIResponse failed:", response.status, errorText);
    throw new Error("Failed to get AI response");
  }
  const { text } = await response.json();
  return text;
}

// NEW helper – asks server to replace <CONTEXT> / <CONTEXT2>
export async function preparePrompt(profileId, prompt) {
  const adminKey = getAdminKey();
  const resp = await fetch(
    `${BASE_URL}/api/ai-profiles/${profileId}/prepare-prompt`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt, adminKey }),
    }
  );
  if (!resp.ok) {
    const errorText = await resp.text();
    console.error("preparePrompt failed:", resp.status, errorText);
    throw new Error("Failed to prepare prompt");
  }
  const { prompt: finalPrompt } = await resp.json();
  return finalPrompt;
}

export async function updateAIProfile(id, name, description, prompt) {
  const adminKey = getAdminKey();
  const response = await fetch(`${BASE_URL}/api/ai-profiles/${id}/update`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name,
      description,
      prompt,
      adminKey,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to update AI profile");
  }

  return response.json();
}

export async function generateAIProfileImage(profileId, imagePrompt) {
  const adminKey = getAdminKey();
  const response = await fetch(
    `${BASE_URL}/api/ai-profiles/${profileId}/generate-image`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        imagePrompt,
        adminKey,
      }),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to generate AI profile image");
  }

  return response.json();
}

export async function trackViewTime(postId, timeInSeconds) {
  const response = await fetch(`${BASE_URL}/api/posts/${postId}/track-view`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ timeInSeconds, userId: getOrCreateUserId() }),
  });
  if (!response.ok) {
    throw new Error("Failed to track view time");
  }
  return response.json();
}

export async function getPostMetrics(postId, adminKey) {
  const response = await fetch(
    `${BASE_URL}/api/posts/${postId}/metrics?adminKey=${adminKey}`
  );
  if (!response.ok) {
    throw new Error("Failed to fetch post metrics");
  }
  return response.json();
}

export async function fetchInfiniteFeed(
  seenIds = [],
  limit = 25,
  followedIds = getFollowedProfiles()
) {
  const adminKey = getAdminKey();
  const seenIdsParam = seenIds.join(",");
  const followedParam = followedIds.join(",");
  const response = await fetch(
    `${BASE_URL}/api/posts/infinite?seenIds=${seenIdsParam}&limit=${limit}` +
      `&followed=${followedParam}` +
      (adminKey ? `&adminKey=${adminKey}` : ""),
    {
      credentials: "include", // ★ send uid cookie
    }
  );
  if (!response.ok) {
    throw new Error("Failed to fetch infinite feed");
  }
  return response.json();
}

export async function subscribeEmail(email) {
  const resp = await fetch(`${BASE_URL}/api/subscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!resp.ok) {
    const { error } = await resp.json().catch(() => ({ error: "Failed" }));
    throw new Error(error || "Failed to subscribe");
  }
  return resp.json(); // { success: true, alreadySubscribed: … }
}

/* ───────── following helpers ───────── */
const FOLLOWED_KEY = "followedProfiles";

export function getFollowedProfiles() {
  try {
    return JSON.parse(localStorage.getItem(FOLLOWED_KEY) || "[]");
  } catch {
    return [];
  }
}

export function toggleFollowProfile(id) {
  const current = new Set(getFollowedProfiles());
  if (current.has(id)) current.delete(id);
  else current.add(id);
  localStorage.setItem(FOLLOWED_KEY, JSON.stringify([...current]));

  /* let the rest of the app know something changed */
  window.dispatchEvent(new Event("followChanged"));
  return [...current];
}

export async function submitProfileSuggestion(data) {
  const resp = await fetch(`${BASE_URL}/api/profile-suggestions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!resp.ok) {
    const { error } = await resp.json().catch(() => ({ error: "Failed" }));
    throw new Error(error || "Submission failed");
  }
  return resp.json(); // { id, ok:true }
}

// --- Profile Suggestions API -----------------------------

export async function fetchProfileSuggestions() {
  const adminKey = getAdminKey();
  const resp = await fetch(
    `${BASE_URL}/api/profile-suggestions${
      adminKey ? `?adminKey=${adminKey}` : ""
    }`
  );
  if (!resp.ok) throw new Error("Failed to fetch profile suggestions");
  return resp.json(); // sanitized list; never includes email/secret
}

export async function deleteProfileSuggestion(id) {
  const adminKey = getAdminKey();
  if (!adminKey) throw new Error("Admin key is required to delete");
  const resp = await fetch(
    `${BASE_URL}/api/profile-suggestions/${id}?adminKey=${adminKey}`,
    { method: "DELETE" }
  );
  if (!resp.ok) {
    const { error } = await resp.json().catch(() => ({ error: "Failed" }));
    throw new Error(error || "Failed to delete");
  }
  return resp.json(); // { ok:true, id }
}
